import { useMemo, useState } from "react";
import { ArrowUpRight, MapPin, Phone, Mail, Clock, Instagram, Facebook } from "lucide-react";
import { toast } from "sonner";
import FadeIn from "../components/FadeIn";
import { CATEGORIES, SERVICES, SPECIALISTS, HOURS_OPTIONS, CONTACT } from "../data/salon";

const TODAY = new Date().toISOString().slice(0, 10);

const initialForm = {
  name: "",
  email: "",
  phone: "",
  service: "",
  specialist: SPECIALISTS[0],
  date: "",
  time: "",
  notes: "",
};

export default function Rezervari() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const allServices = useMemo(() => {
    const list = [];
    CATEGORIES.forEach((c) => {
      (SERVICES[c.id] || []).forEach((s) => {
        list.push({ label: `${c.label} · ${s.name}`, value: `${c.label} — ${s.name}` });
      });
    });
    return list;
  }, []);

  const setField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Introdu numele complet.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) er.email = "Email invalid.";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 9) er.phone = "Telefon invalid.";
    if (!form.service) er.service = "Selectează un serviciu.";
    if (!form.date) er.date = "Alege o dată.";
    if (!form.time) er.time = "Alege o oră.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Verifică câmpurile completate.", {
        description: "Câteva detalii lipsesc sau sunt invalide.",
      });
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      toast.success("Cererea a fost trimisă cu succes!", {
        description: `Vei fi contactată în maxim 2 ore pentru confirmare, ${form.name.split(" ")[0]}.`,
        duration: 6000,
      });
      setForm(initialForm);
    }, 900);
  };

  return (
    <main data-testid="reservations-page">
      {/* Hero */}
      <section className="pt-40 pb-16 md:pt-48 md:pb-20 marble-soft">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 text-center">
          <FadeIn>
            <p className="eyebrow mb-6">— Rezervări —</p>
          </FadeIn>
          <FadeIn delay={120}>
            <h1
              data-testid="reservations-headline"
              className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05]"
              style={{ color: "#1A1A1A", fontWeight: 300 }}
            >
              Programează-ți <span style={{ fontStyle: "italic", color: "#B8964E" }}>momentul</span>.
            </h1>
          </FadeIn>
          <FadeIn delay={240}>
            <p className="mt-8 max-w-2xl mx-auto text-lg leading-relaxed" style={{ color: "#555" }}>
              Completează formularul de mai jos și vei fi contactată în maxim 2
              ore pentru confirmarea programării.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Form section */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12">
          <FadeIn>
            <form
              onSubmit={onSubmit}
              data-testid="reservation-form"
              className="bg-white p-8 md:p-14"
              style={{ border: "1px solid #E8E0D5" }}
              noValidate
            >
              <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
                <div>
                  <p className="eyebrow mb-3">— Cerere de programare —</p>
                  <h2 className="font-serif-display text-3xl sm:text-4xl" style={{ color: "#1A1A1A", fontWeight: 300 }}>
                    Detaliile <span style={{ fontStyle: "italic", color: "#B8964E" }}>tale</span>
                  </h2>
                </div>
                <p className="text-xs tracking-luxe" style={{ color: "#999" }}>
                  TOATE CÂMPURILE SUNT IMPORTANTE
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                {/* Nume */}
                <div>
                  <label className="lux-label" htmlFor="name">Nume complet *</label>
                  <input
                    id="name"
                    type="text"
                    data-testid="reservation-name-input"
                    className="lux-input"
                    placeholder="Ex: Andreea Popescu"
                    value={form.name}
                    onChange={setField("name")}
                  />
                  {errors.name && <p className="text-xs mt-2" style={{ color: "#c0392b" }}>{errors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="lux-label" htmlFor="email">Email *</label>
                  <input
                    id="email"
                    type="email"
                    data-testid="reservation-email-input"
                    className="lux-input"
                    placeholder="nume@email.ro"
                    value={form.email}
                    onChange={setField("email")}
                  />
                  {errors.email && <p className="text-xs mt-2" style={{ color: "#c0392b" }}>{errors.email}</p>}
                </div>

                {/* Telefon */}
                <div>
                  <label className="lux-label" htmlFor="phone">Telefon *</label>
                  <input
                    id="phone"
                    type="tel"
                    data-testid="reservation-phone-input"
                    className="lux-input"
                    placeholder="+40 7XX XXX XXX"
                    value={form.phone}
                    onChange={setField("phone")}
                  />
                  {errors.phone && <p className="text-xs mt-2" style={{ color: "#c0392b" }}>{errors.phone}</p>}
                </div>

                {/* Serviciu */}
                <div>
                  <label className="lux-label" htmlFor="service">Serviciu dorit *</label>
                  <select
                    id="service"
                    data-testid="reservation-service-input"
                    className="lux-input"
                    value={form.service}
                    onChange={setField("service")}
                    style={{ appearance: "none", cursor: "pointer", color: form.service ? "#1A1A1A" : "#999" }}
                  >
                    <option value="">Alege un serviciu...</option>
                    {CATEGORIES.map((c) => (
                      <optgroup key={c.id} label={c.label}>
                        {(SERVICES[c.id] || []).map((s) => (
                          <option key={s.name} value={`${c.label} — ${s.name}`}>
                            {s.name} · {s.price} RON
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {errors.service && <p className="text-xs mt-2" style={{ color: "#c0392b" }}>{errors.service}</p>}
                </div>

                {/* Specialist */}
                <div>
                  <label className="lux-label" htmlFor="specialist">Specialist preferat</label>
                  <select
                    id="specialist"
                    data-testid="reservation-specialist-input"
                    className="lux-input"
                    value={form.specialist}
                    onChange={setField("specialist")}
                    style={{ appearance: "none", cursor: "pointer" }}
                  >
                    {SPECIALISTS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                {/* Date */}
                <div>
                  <label className="lux-label" htmlFor="date">Data preferată *</label>
                  <input
                    id="date"
                    type="date"
                    min={TODAY}
                    data-testid="reservation-date-input"
                    className="lux-input"
                    value={form.date}
                    onChange={setField("date")}
                    style={{ cursor: "pointer", color: form.date ? "#1A1A1A" : "#999" }}
                  />
                  {errors.date && <p className="text-xs mt-2" style={{ color: "#c0392b" }}>{errors.date}</p>}
                </div>

                {/* Time */}
                <div>
                  <label className="lux-label" htmlFor="time">Ora preferată *</label>
                  <select
                    id="time"
                    data-testid="reservation-time-input"
                    className="lux-input"
                    value={form.time}
                    onChange={setField("time")}
                    style={{ appearance: "none", cursor: "pointer", color: form.time ? "#1A1A1A" : "#999" }}
                  >
                    <option value="">Alege un interval...</option>
                    {HOURS_OPTIONS.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                  {errors.time && <p className="text-xs mt-2" style={{ color: "#c0392b" }}>{errors.time}</p>}
                </div>

                {/* Spacer for alignment */}
                <div className="hidden md:block" />

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="lux-label" htmlFor="notes">Mențiuni</label>
                  <textarea
                    id="notes"
                    rows={4}
                    data-testid="reservation-notes-input"
                    className="lux-input"
                    placeholder="Lungime păr, preferințe culoare, alergii sau orice detaliu util..."
                    value={form.notes}
                    onChange={setField("notes")}
                    style={{ resize: "vertical", paddingTop: "0.85rem" }}
                  />
                </div>
              </div>

              <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <p className="text-sm italic" style={{ color: "#999" }}>
                  Vei fi contactată în maxim 2 ore pentru confirmare.
                </p>
                <button
                  type="submit"
                  data-testid="reservation-submit-button"
                  disabled={submitting}
                  className="btn-lux"
                  style={
                    submitting
                      ? { backgroundColor: "#B8964E", color: "#fff" }
                      : {}
                  }
                >
                  {submitting ? "Se trimite..." : "Trimite cererea"}
                  <ArrowUpRight size={14} />
                </button>
              </div>
            </form>
          </FadeIn>
        </div>
      </section>

      {/* Split: Map + Contact */}
      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Map placeholder */}
            <FadeIn>
              <div
                className="relative overflow-hidden"
                style={{ aspectRatio: "4/3", border: "1px solid #E8E0D5" }}
                data-testid="map-placeholder"
              >
                <iframe
                  title="Lumière Beauty Studio — Strada Eroilor 14, Cluj-Napoca"
                  src="https://www.google.com/maps?q=Strada+Eroilor+14+Cluj-Napoca&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: "grayscale(0.4) contrast(0.95)" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <div
                  className="absolute bottom-4 left-4 right-4 sm:left-6 sm:right-auto bg-white px-5 py-4"
                  style={{ border: "1px solid #E8E0D5", maxWidth: 320 }}
                >
                  <p className="eyebrow" style={{ color: "#B8964E" }}>Studio</p>
                  <p className="font-serif-display text-xl mt-1" style={{ color: "#1A1A1A" }}>
                    Strada Eroilor 14
                  </p>
                  <p className="text-sm" style={{ color: "#555" }}>Cluj-Napoca, România</p>
                </div>
              </div>
            </FadeIn>

            {/* Contact info */}
            <FadeIn delay={150}>
              <div className="h-full flex flex-col justify-center">
                <p className="eyebrow mb-4">— Te așteptăm —</p>
                <h2 className="font-serif-display text-4xl sm:text-5xl" style={{ color: "#1A1A1A", fontWeight: 300 }}>
                  Vino să ne <span style={{ fontStyle: "italic", color: "#B8964E" }}>cunoaștem</span>.
                </h2>

                <ul className="mt-10 flex flex-col gap-6">
                  <li className="flex items-start gap-5">
                    <div className="shrink-0 w-11 h-11 flex items-center justify-center" style={{ border: "1px solid #B8964E", color: "#B8964E" }}>
                      <MapPin size={16} />
                    </div>
                    <div>
                      <p className="eyebrow" style={{ color: "#999" }}>Adresă</p>
                      <p className="mt-1 text-base" style={{ color: "#1A1A1A" }}>{CONTACT.address}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-5">
                    <div className="shrink-0 w-11 h-11 flex items-center justify-center" style={{ border: "1px solid #B8964E", color: "#B8964E" }}>
                      <Phone size={16} />
                    </div>
                    <div>
                      <p className="eyebrow" style={{ color: "#999" }}>Telefon</p>
                      <a
                        href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                        className="mt-1 block text-base"
                        style={{ color: "#1A1A1A" }}
                        data-testid="contact-phone-link"
                      >
                        {CONTACT.phone}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-5">
                    <div className="shrink-0 w-11 h-11 flex items-center justify-center" style={{ border: "1px solid #B8964E", color: "#B8964E" }}>
                      <Mail size={16} />
                    </div>
                    <div>
                      <p className="eyebrow" style={{ color: "#999" }}>Email</p>
                      <a
                        href={`mailto:${CONTACT.email}`}
                        className="mt-1 block text-base"
                        style={{ color: "#1A1A1A" }}
                        data-testid="contact-email-link"
                      >
                        {CONTACT.email}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-5">
                    <div className="shrink-0 w-11 h-11 flex items-center justify-center" style={{ border: "1px solid #B8964E", color: "#B8964E" }}>
                      <Clock size={16} />
                    </div>
                    <div>
                      <p className="eyebrow" style={{ color: "#999" }}>Program</p>
                      <ul className="mt-1 flex flex-col gap-0.5">
                        {CONTACT.hoursShort.map((h) => (
                          <li key={h} className="text-base" style={{ color: "#1A1A1A" }}>{h}</li>
                        ))}
                      </ul>
                    </div>
                  </li>
                </ul>

                <div className="mt-10 pt-8 flex gap-3" style={{ borderTop: "1px solid #E8E0D5" }}>
                  <a
                    href={CONTACT.instagram}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Instagram"
                    data-testid="contact-instagram"
                    className="w-11 h-11 flex items-center justify-center transition-colors"
                    style={{ border: "1px solid #E8E0D5", color: "#1A1A1A" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#B8964E";
                      e.currentTarget.style.color = "#fff";
                      e.currentTarget.style.borderColor = "#B8964E";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "#1A1A1A";
                      e.currentTarget.style.borderColor = "#E8E0D5";
                    }}
                  >
                    <Instagram size={16} />
                  </a>
                  <a
                    href={CONTACT.facebook}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Facebook"
                    data-testid="contact-facebook"
                    className="w-11 h-11 flex items-center justify-center transition-colors"
                    style={{ border: "1px solid #E8E0D5", color: "#1A1A1A" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#B8964E";
                      e.currentTarget.style.color = "#fff";
                      e.currentTarget.style.borderColor = "#B8964E";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "#1A1A1A";
                      e.currentTarget.style.borderColor = "#E8E0D5";
                    }}
                  >
                    <Facebook size={16} />
                  </a>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </main>
  );
}
